import { useState, useMemo } from "react";
import { EventCard } from "../components/events/EventCard";
import { EventFilters } from "../components/events/EventFilters";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { useEventFiltersStore } from "../stores/event-filters-store";
import { List, Grid, Calendar, Loader2 } from "lucide-react";
import { useEvents } from "../lib/hooks/useEvents";
import { useDebounce } from "../lib/hooks/useDebounce";
import { getDateRangeFromFilter, toISOString } from "../lib/date-utils";
import { extractUniqueTags } from "../lib/utils";
import type { Event } from "../lib/types";

export const EventsList = () => {
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const { search, selectedTags, dateFilter } = useEventFiltersStore();

  // Debounce search input to prevent excessive API calls
  const debouncedSearch = useDebounce(search, 300);

  // Calculate date range from filter option
  const dateRange = useMemo(
    () => getDateRangeFromFilter(dateFilter),
    [dateFilter],
  );

  // Memoize query parameters so React Query can detect changes
  const queryParams = useMemo(() => {
    const filterParts: string[] = [
      "isBookable==true"
    ];

    // Add date range filters to RSQL filter string
    if (dateRange) {
      filterParts.push(`startsAt>=${JSON.stringify(toISOString(dateRange.from))}`);
      filterParts.push(`startsAt<=${JSON.stringify(toISOString(dateRange.to))}`);
    }

    if (selectedTags.length > 0) {
      // Filter by tag names via RSQL relation handler in the public events API
      filterParts.push(`tags=in=${JSON.stringify(selectedTags.join(","))}`);
    }

    if (debouncedSearch && debouncedSearch.trim()) {
      filterParts.push(`title=like="${debouncedSearch.trim()}"`);
    }

    return {
      page: 1,
      pageSize: 100, // Large page size to get all events
      filter: filterParts.join(";"),
      beforeBookingCloses: true,
    };
  }, [dateRange, selectedTags, debouncedSearch]);

  // Fetch events from API with filters
  const {
    data: eventsResponse,
    isLoading,
    error,
  } = useEvents(queryParams);

  // Extract events from API response
  const allEvents = eventsResponse?.data || [];

  // Events page shows only bookable events. Cancelled events are still shown (but not bookable).
  const events = useMemo(
    () =>
      allEvents.filter((event: Event) => event.isBookable),
    [allEvents],
  );

  const allTags = useMemo(() => extractUniqueTags(events), [events]);

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <span className="ml-2 text-gray-600">Loading events...</span>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Error loading events
        </h3>
        <p className="text-gray-600">Something went wrong. Please try again.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Events</h1>
          <p className="text-gray-600 mt-1">
            A family-orientated club that sails throughout the year
          </p>
        </div>

        <div className="hidden md:flex items-center gap-2">
          <Button
            variant={viewMode === "grid" ? "primary" : "outline"}
            size="sm"
            onClick={() => setViewMode("grid")}
          >
            <Grid className="w-4 h-4" />
          </Button>
          <Button
            variant={viewMode === "list" ? "primary" : "outline"}
            size="sm"
            onClick={() => setViewMode("list")}
          >
            <List className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Filters Sidebar */}
        <div className="lg:col-span-1">
          <EventFilters availableTags={allTags} />
        </div>

        {/* Events Grid/List */}
        <div className="lg:col-span-3">
          {events.length === 0 ? (
            <Card>
              <div className="text-center py-12">
                <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No events found
                </h3>
                <p className="text-gray-600">
                  Try adjusting your filters to see more events.
                </p>
              </div>
            </Card>
          ) : (
            <div
              className={
                viewMode === "grid"
                  ? "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 items-stretch"
                  : "space-y-4"
              }
            >
              {events.map((event: Event) => (
                <EventCard key={event.id} event={event} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
